/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '.'
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import DisplayPrice from '../display-price'

const PricePerItem = ({currency, basePrice}) => {
    return (
        <Text fontSize={{base: '12px', lg: '14px'}}>
            <FormattedNumber style="currency" currency={currency} value={basePrice} />
            <FormattedMessage
                defaultMessage="ea"
                id="price_per_item.label.each"
                description="Abbreviated 'each', follows price per item, like $10/ea"
            />
        </Text>
    )
}

PricePerItem.propTypes = {
    currency: PropTypes.string.isRequired,
    basePrice: PropTypes.number
}

/**
 * In the context of a cart product item variant, this component renders the item's
 * pricing, taking into account applied discounts as well as base item prices.
 */
const ItemPrice = ({
    currency,
    align = 'right',
    baseDirection = 'column',
    fontSize = 'sm',
    isDisplayPricePerUnit = false,
    showOnlyItemTotal = false,
    ...props
}) => {
    const variant = useItemVariant()
    const [priceData, setPriceData] = useState(null)

    // When variant has basket pricing, we should prioritize these prices for displaying
    // since they may have take promotion/discount into account
    // NOTE: try NOT to re-calculate these values since basket-level discount is complicated
    useEffect(() => {
        if (variant?.itemId) {
            const {c_listPrice, price, priceAfterItemDiscount} = variant
            const isASet = variant?.type?.set
            const isMaster = variant?.type?.master
            let currentPrice = priceAfterItemDiscount
            let pricePerUnit = variant?.pricePerUnit
            let listPrice = c_listPrice || price

            if (
                variant?.priceAdjustments &&
                variant.priceAdjustments.length > 0 &&
                variant.priceAfterItemDiscount
            ) {
                // Calculate the discounted price per unit
                listPrice = listPrice / (variant.quantity || 1)
                pricePerUnit = variant.priceAfterItemDiscount / (variant.quantity || 1)
            }

            setPriceData({
                currentPrice: currentPrice,
                listPrice: listPrice,
                pricePerUnit: pricePerUnit,
                isASet,
                isMaster,
                isRange: isASet || isMaster || false,
                isOnSale: (c_listPrice > variant?.basePrice) || (price > priceAfterItemDiscount)
            })
        } else {
            setPriceData(getPriceData(variant))
        }
    }, [variant])

    return (
        <Stack
            textAlign={align}
            direction={baseDirection}
            justifyContent={align === 'left' ? 'flex-start' : 'flex-end'}
            alignItems="baseline"
            spacing={priceData?.isOnSale ? 0 : 1}
            wrap="nowrap"
            {...props}
        >
            {variant?.itemId ? (
                showOnlyItemTotal ? (
                    <Text as={'span'}>
                        <FormattedNumber style="currency" currency={currency} value={priceData?.currentPrice} />
                    </Text>
                ) : (
                    priceData && <DisplayPrice
                        labelForA11y={variant?.name}
                        currency={currency}
                        priceData={priceData}
                        listPriceProps={{fontSize, lineHeight: '18px'}}
                        currentPriceProps={{fontSize, lineHeight: '18px'}}
                        isDisplayPricePerUnit={isDisplayPricePerUnit}
                    />
                )
            ) : (
                priceData && <DisplayPrice
                    labelForA11y={variant?.name}
                    currency={currency}
                    priceData={priceData}
                    quantity={variant?.quantity}
                    listPriceProps={{fontSize, lineHeight: '18px'}}
                    currentPriceProps={{fontSize, lineHeight: '18px'}}
                    isDisplayPricePerUnit={isDisplayPricePerUnit}
                />
            )}
        </Stack>
    )
}

ItemPrice.propTypes = {
    currency: PropTypes.string.isRequired,
    align: PropTypes.string,
    baseDirection: PropTypes.string,
    fontSize: PropTypes.string,
    isDisplayPricePerUnit: PropTypes.bool
}

export default ItemPrice
